import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import {
    Lock,
    LogOut,
    Mail,
    Phone,
    Trash2,
    CheckCircle2,
    Circle,
    RefreshCw,
    Inbox,
} from "lucide-react";
import { EASE } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
    "w-full border border-line bg-night/60 px-4 py-3.5 text-sm text-white placeholder:text-slate-500 outline-none transition-colors duration-300 focus:border-accent";
const labelCls = "mb-2 block font-mono text-[0.65rem] uppercase tracking-[0.25em] text-slate-400";

function formatApiErrorDetail(detail) {
    if (detail == null) return "Something went wrong. Please try again.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail)) {
        return detail
            .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
            .filter(Boolean)
            .join(" ");
    }
    if (detail && typeof detail.msg === "string") return detail.msg;
    return String(detail);
}

function LoginCard({ onSuccess }) {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [busy, setBusy] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        setError("");
        try {
            const res = await axios.post(
                `${API}/auth/login`,
                { email, password },
                { withCredentials: true }
            );
            onSuccess(res.data);
        } catch (err) {
            setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="flex min-h-[82vh] items-center justify-center px-4 pb-16 pt-28">
            <motion.form
                onSubmit={submit}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, ease: EASE }}
                data-testid="admin-login-form"
                className="w-full max-w-md border border-line bg-card p-8 sm:p-10"
            >
                <span className="flex h-12 w-12 items-center justify-center border border-accent/40 bg-night/60 text-accent">
                    <Lock className="h-5 w-5" />
                </span>
                <h1 className="mt-6 font-display text-3xl font-bold uppercase text-white">
                    Owner Login
                </h1>
                <p className="mt-2 text-sm text-slate-400">
                    Sign in to manage website enquiries.
                </p>
                <div className="mt-8">
                    <label htmlFor="admin-email" className={labelCls}>
                        Email
                    </label>
                    <input
                        id="admin-email"
                        type="email"
                        required
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        data-testid="admin-login-email"
                        placeholder="you@example.com"
                        className={inputCls}
                    />
                </div>
                <div className="mt-5">
                    <label htmlFor="admin-password" className={labelCls}>
                        Password
                    </label>
                    <input
                        id="admin-password"
                        type="password"
                        required
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        data-testid="admin-login-password"
                        placeholder="Password"
                        className={inputCls}
                    />
                </div>
                {error && (
                    <p
                        data-testid="admin-login-error"
                        className="mt-5 border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-300"
                    >
                        {error}
                    </p>
                )}
                <button
                    type="submit"
                    disabled={busy}
                    data-testid="admin-login-btn"
                    className="mt-8 w-full bg-accent px-8 py-4 font-display text-base font-bold uppercase tracking-widest text-night transition-colors duration-300 hover:bg-accent-hover disabled:cursor-not-allowed disabled:opacity-60"
                >
                    {busy ? "Signing in..." : "Sign In"}
                </button>
            </motion.form>
        </div>
    );
}

function EnquiryCard({ enq, index, onToggle, onDelete }) {
    const received = new Date(enq.created_at).toLocaleString("en-IN", {
        dateStyle: "medium",
        timeStyle: "short",
    });
    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: Math.min(index * 0.05, 0.3), ease: EASE }}
            data-testid={`admin-enquiry-card-${index}`}
            className={`border bg-card p-6 transition-colors sm:p-7 ${
                enq.handled ? "border-line/60 opacity-70" : "border-line"
            }`}
        >
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                    <div className="flex flex-wrap items-center gap-3">
                        <h3 className="font-display text-2xl font-bold uppercase text-white">
                            {enq.name}
                        </h3>
                        <span className="bg-accent/15 px-2.5 py-1 font-mono text-[0.6rem] uppercase tracking-[0.2em] text-accent">
                            {enq.service}
                        </span>
                        {!enq.handled && (
                            <span
                                data-testid={`admin-enquiry-new-badge-${index}`}
                                className="border border-accent/50 px-2.5 py-1 font-mono text-[0.6rem] uppercase tracking-[0.2em] text-accent"
                            >
                                New
                            </span>
                        )}
                    </div>
                    <p className="mt-1.5 font-mono text-xs text-slate-500">{received}</p>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        onClick={() => onToggle(enq)}
                        data-testid={`admin-toggle-handled-${index}`}
                        title={enq.handled ? "Mark as new" : "Mark as handled"}
                        className="flex h-10 w-10 items-center justify-center border border-line text-slate-400 transition-colors hover:border-accent hover:text-accent"
                    >
                        {enq.handled ? (
                            <CheckCircle2 className="h-4 w-4 text-accent" />
                        ) : (
                            <Circle className="h-4 w-4" />
                        )}
                    </button>
                    <button
                        onClick={() => onDelete(enq)}
                        data-testid={`admin-delete-${index}`}
                        title="Delete enquiry"
                        className="flex h-10 w-10 items-center justify-center border border-line text-slate-400 transition-colors hover:border-red-500 hover:text-red-400"
                    >
                        <Trash2 className="h-4 w-4" />
                    </button>
                </div>
            </div>
            <p className="mt-4 text-sm leading-relaxed text-slate-300">{enq.message}</p>
            <div className="mt-5 flex flex-wrap gap-x-6 gap-y-2 border-t border-line/70 pt-4">
                <a
                    href={`tel:${enq.phone.replace(/[^+\d]/g, "")}`}
                    data-testid={`admin-enquiry-phone-${index}`}
                    className="flex items-center gap-2 font-mono text-sm text-slate-300 transition-colors hover:text-accent"
                >
                    <Phone className="h-3.5 w-3.5 text-accent" />
                    {enq.phone}
                </a>
                <a
                    href={`mailto:${enq.email}`}
                    data-testid={`admin-enquiry-email-${index}`}
                    className="flex items-center gap-2 break-all font-mono text-sm text-slate-300 transition-colors hover:text-accent"
                >
                    <Mail className="h-3.5 w-3.5 text-accent" />
                    {enq.email}
                </a>
            </div>
        </motion.div>
    );
}

function Dashboard({ user, onLogout }) {
    const [enquiries, setEnquiries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("all");

    const load = async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${API}/admin/enquiries`, { withCredentials: true });
            setEnquiries(res.data.enquiries || []);
        } catch (e) {
            if (e?.response?.status === 401) onLogout();
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const toggle = async (enq) => {
        setEnquiries((l) =>
            l.map((x) => (x.id === enq.id ? { ...x, handled: !x.handled } : x))
        );
        try {
            await axios.patch(
                `${API}/admin/enquiries/${enq.id}`,
                { handled: !enq.handled },
                { withCredentials: true }
            );
        } catch {
            load();
        }
    };

    const remove = async (enq) => {
        if (!window.confirm(`Delete the enquiry from ${enq.name}?`)) return;
        setEnquiries((l) => l.filter((x) => x.id !== enq.id));
        try {
            await axios.delete(`${API}/admin/enquiries/${enq.id}`, { withCredentials: true });
        } catch {
            load();
        }
    };

    const shown = enquiries.filter((e) =>
        filter === "new" ? !e.handled : filter === "handled" ? e.handled : true
    );
    const newCount = enquiries.filter((e) => !e.handled).length;

    const filters = [
        { key: "all", label: `All (${enquiries.length})` },
        { key: "new", label: `New (${newCount})` },
        { key: "handled", label: `Handled (${enquiries.length - newCount})` },
    ];

    return (
        <div className="mx-auto w-full max-w-5xl px-4 pb-24 pt-28 sm:px-6 lg:px-8">
            <div className="flex flex-wrap items-center justify-between gap-4 border-b border-line pb-6">
                <div>
                    <p className="font-mono text-xs uppercase tracking-[0.3em] text-accent">
                        Enquiry Admin
                    </p>
                    <h1 className="mt-2 font-display text-4xl font-bold uppercase text-white">
                        Stored Enquiries
                    </h1>
                    <p className="mt-1 font-mono text-xs text-slate-500">Signed in as {user.email}</p>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        onClick={load}
                        data-testid="admin-refresh-btn"
                        title="Refresh"
                        className="flex h-11 w-11 items-center justify-center border border-line text-slate-300 transition-colors hover:border-accent hover:text-accent"
                    >
                        <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
                    </button>
                    <button
                        onClick={onLogout}
                        data-testid="admin-logout-btn"
                        className="flex items-center gap-2 border border-line px-5 py-3 font-display text-xs font-bold uppercase tracking-widest text-slate-300 transition-colors hover:border-accent hover:text-accent"
                    >
                        <LogOut className="h-4 w-4" />
                        Logout
                    </button>
                </div>
            </div>

            <div className="mt-6 flex flex-wrap gap-2">
                {filters.map((f) => (
                    <button
                        key={f.key}
                        onClick={() => setFilter(f.key)}
                        data-testid={`admin-filter-${f.key}`}
                        className={`px-5 py-2.5 font-mono text-xs uppercase tracking-[0.2em] transition-colors ${
                            filter === f.key
                                ? "bg-accent text-night"
                                : "border border-line text-slate-400 hover:border-accent hover:text-accent"
                        }`}
                    >
                        {f.label}
                    </button>
                ))}
            </div>

            <div className="mt-8 space-y-5">
                {loading ? (
                    <p data-testid="admin-loading" className="py-16 text-center font-mono text-sm text-slate-500">
                        Loading enquiries...
                    </p>
                ) : shown.length === 0 ? (
                    <div
                        data-testid="admin-empty-state"
                        className="flex flex-col items-center gap-4 border border-dashed border-line py-20 text-center"
                    >
                        <Inbox className="h-10 w-10 text-slate-600" />
                        <p className="font-display text-2xl font-bold uppercase text-slate-400">
                            {enquiries.length === 0
                                ? "No enquiries yet"
                                : "Nothing in this view"}
                        </p>
                        <p className="max-w-sm text-sm text-slate-500">
                            Enquiries submitted through the website contact form appear here.
                        </p>
                    </div>
                ) : (
                    shown.map((enq, i) => (
                        <EnquiryCard
                            key={enq.id}
                            enq={enq}
                            index={i}
                            onToggle={toggle}
                            onDelete={remove}
                        />
                    ))
                )}
            </div>
        </div>
    );
}

export default function Admin() {
    const [user, setUser] = useState(null); // null = checking, false = signed out, object = signed in

    useEffect(() => {
        axios
            .get(`${API}/auth/me`, { withCredentials: true })
            .then((res) => setUser(res.data))
            .catch(() => setUser(false));
    }, []);

    const logout = async () => {
        try {
            await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
        } finally {
            setUser(false);
        }
    };

    return (
        <main data-testid="admin-page" className="min-h-screen">
            {user === null ? (
                <div className="flex min-h-[82vh] items-center justify-center">
                    <p className="font-mono text-sm text-slate-500">Checking session...</p>
                </div>
            ) : user ? (
                <Dashboard user={user} onLogout={logout} />
            ) : (
                <LoginCard onSuccess={setUser} />
            )}
        </main>
    );
}
